import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Table, Tag, Progress, Row, Col, Statistic, Typography, Space, Button, notification } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';

import request from '../lib/request';
import { TASK_STATUS_COLORS, DueDate } from '../screens/main/TasksPage';

const RAG = { GREEN: { color: 'green', text: 'On track' }, AMBER: { color: 'orange', text: 'Due within a week' }, RED: { color: 'red', text: 'Overdue items' } };

/** "Workbook status" of a client (xlsx MENU Client -> Workbook status): projects, open tasks, deadlines, engagements. */
export default function ClientWorkbookTab({ clientId }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const load = React.useCallback(() => {
    setLoading(true);
    request.get(`/workbook/clients/${clientId}`).then((r) => r.json()).then((r) => (r.status ? setData(r.data) : notification.error({ message: r.message }))).finally(() => setLoading(false));
  }, [clientId]);
  React.useEffect(load, [load]);
  if (!data) return null;
  const s = data.summary;
  const rag = RAG[s.rag];
  return (
    <div>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Space>
          <Tag color={rag.color} style={{ fontSize: 13, padding: '2px 10px' }}>{rag.text}</Tag>
          <Typography.Text type="secondary">Work position as at {data.today}. Open the <Link to={`/workbook?client=${clientId}`}>work board</Link> for this client.</Typography.Text>
        </Space>
        <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
      </Space>
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={4}><Card size="small"><Statistic title="Open projects" value={s.open_projects} suffix={<Typography.Text type="secondary" style={{ fontSize: 12 }}>/ {s.completed_projects} done</Typography.Text>} /></Card></Col>
        <Col xs={12} md={4}><Card size="small"><Statistic title="Open tasks" value={s.open_tasks} /></Card></Col>
        <Col xs={12} md={4}><Card size="small"><Statistic title="Overdue tasks" value={s.overdue_tasks} valueStyle={{ color: s.overdue_tasks ? '#cf1322' : undefined }} /></Card></Col>
        <Col xs={12} md={4}><Card size="small"><Statistic title="Pending deadlines" value={s.pending_deadlines} /></Card></Col>
        <Col xs={12} md={4}><Card size="small"><Statistic title="Overdue deadlines" value={s.overdue_deadlines} valueStyle={{ color: s.overdue_deadlines ? '#cf1322' : undefined }} /></Card></Col>
        <Col xs={12} md={4}><Card size="small"><Statistic title="Active engagements" value={s.active_engagements} /></Card></Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} xl={12}>
          <Card size="small" title={`Projects (${data.projects.length})`} style={{ marginBottom: 16 }}>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={data.projects}
              locale={{ emptyText: 'No projects yet - activate a service engagement to generate one.' }}
              columns={[
                { title: 'Project', dataIndex: 'name', render: (v, p) => <span>{v} {p.status === 'COMPLETED' && <Tag color="green">done</Tag>}</span> },
                { title: 'Progress', dataIndex: 'progress', width: 140, render: (v, p) => <Progress percent={v} size="small" status={p.overdue ? 'exception' : v === 100 ? 'success' : 'normal'} /> },
                { title: 'Tasks', key: 't', width: 110, render: (_, p) => <span>{p.completed}/{p.tasks}{p.overdue ? <Typography.Text type="danger"> ({p.overdue} overdue)</Typography.Text> : ''}</span> },
                { title: 'Next due', dataIndex: 'next_due', width: 100, render: (v) => (v ? <span style={{ color: v < data.today ? '#cf1322' : undefined }}>{moment(v).format('DD MMM YY')}</span> : '-') },
              ]}
            />
          </Card>
          <Card size="small" title={`Service engagements (${data.engagements.length})`}>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={data.engagements}
              locale={{ emptyText: 'No engagements' }}
              columns={[
                { title: 'Service', key: 's', render: (_, e) => <span>{e.mnemonic && <Tag>{e.mnemonic}</Tag>}{e.service}{e.entity_name ? <Typography.Text type="secondary"> - {e.entity_name}</Typography.Text> : ''}</span> },
                { title: 'Frequency', dataIndex: 'frequency', width: 100, render: (v) => v.replace('_', '-').toLowerCase() },
                { title: 'Last period', dataIndex: 'last_generated_period', width: 100, render: (v) => v || '-' },
                { title: 'Status', dataIndex: 'status', width: 90, render: (v) => <Tag color={v === 'ACTIVE' ? 'green' : v === 'PAUSED' ? 'orange' : 'default'}>{v}</Tag> },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card size="small" title={`Open tasks (${data.tasks.length})`} style={{ marginBottom: 16 }}>
            <Table
              size="small"
              rowKey="id"
              pagination={{ pageSize: 10, size: 'small', hideOnSinglePage: true }}
              dataSource={data.tasks}
              locale={{ emptyText: 'No open tasks' }}
              columns={[
                { title: 'Task', dataIndex: 'subject', render: (v, t) => <Link to={`/tasks/${t.id}`}>{v}</Link> },
                { title: 'Project', key: 'p', render: (_, t) => t.Project?.name || '-' },
                { title: 'Who', key: 'w', width: 120, render: (_, t) => t.AssignedUser?.name || <Typography.Text type="secondary">unassigned</Typography.Text> },
                { title: 'Due', key: 'd', width: 100, render: (_, t) => <DueDate task={t} /> },
                { title: 'Status', dataIndex: 'status', width: 100, render: (v) => <Tag color={TASK_STATUS_COLORS[v]}>{v}</Tag> },
              ]}
            />
          </Card>
          <Card size="small" title={`Pending deadlines (${data.deadlines.length})`}>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={data.deadlines}
              locale={{ emptyText: 'No pending deadlines' }}
              columns={[
                { title: 'Due', dataIndex: 'due_date', width: 100, render: (v, d) => <span style={{ color: d.overdue ? '#cf1322' : undefined, fontWeight: d.overdue ? 600 : 400 }}>{moment(v).format('DD MMM YY')}</span> },
                { title: 'Deadline', key: 'd', render: (_, d) => <span>{d.deadline_type}{d.period ? ` ${d.period}` : ''}{d.entity_name ? <Typography.Text type="secondary"> - {d.entity_name}</Typography.Text> : ''}</span> },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
